import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Printer, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import OspiteSearchSelect from "./OspiteSearchSelect";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Stampa118Dialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const [ospiti, setOspiti] = useState([]);
  const [ospiteId, setOspiteId] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setOspiti(res.data || []))
      .catch(() => toast.error("Errore nel caricamento degli ospiti"));
  }, [open, token]);

  const reset = () => setOspiteId("");

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handlePrint = async () => {
    if (!ospiteId) {
      toast.error("Seleziona un ospite");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/fascicoli-assistenziali/${ospiteId}/118.pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );

      const selected = ospiti.find((o) => o.id === ospiteId);
      const a = selected?.anagrafica || {};
      const slug = `${(a.cognome || selected?.cognome || "").trim()}_${(a.nome || selected?.nome || "").trim()}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "")
        .slice(0, 80);

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = slug ? `118_${slug}.pdf` : "118_scheda_emergenza.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      toast.success("PDF generato — puoi condividerlo con il centralino 118 o i soccorsi");
      handleClose(false);
    } catch (err) {
      console.error(err);
      let detailMsg;
      if (err.response?.data instanceof Blob) {
        try {
          const t = await err.response.data.text();
          detailMsg = JSON.parse(t)?.detail;
        } catch {
          detailMsg = null;
        }
      } else {
        detailMsg = err.response?.data?.detail;
      }
      toast.error(detailMsg || "Impossibile generare il PDF. Riprova.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">118</span>
            Stampa scheda ospite (118)
          </DialogTitle>
          <DialogDescription>
            Seleziona l'ospite e genera la scheda emergenza sanitaria per il 118.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Ospite *</Label>
            <OspiteSearchSelect ospiti={ospiti} value={ospiteId} onChange={setOspiteId} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={generating}>Annulla</Button>
          <Button onClick={handlePrint} disabled={generating || !ospiteId} className="bg-red-600 hover:bg-red-700">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
            Genera PDF 118
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
